import React, { useCallback } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Button } from "react-bootstrap";
import { InputBox } from ".";
import { StockModal } from "./designer/StockModal";

interface Props {
  startingUrl: string,
  updatedFunction: (dataUrl: string) => void,
  aspectRatio: number;
  name: string;
  height?: number;
}

export const ImageEditor: React.FC<Props> = (props) => {
  const [currentUrl, setCurrentUrl] = React.useState("about:blank");
  const [dataUrl, setDataUrl] = React.useState(null);
  const [showStock, setShowStock] = React.useState(false);
  let timeout: number | null = null;

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    let files;
    if (e.target) files = e.target.files;
    const reader = new FileReader();
    reader.onload = () => {
      let url = reader.result.toString();
      setCurrentUrl(url);
      setDataUrl(url);
      setTimeout(selectDefaultCropZone, 500);
    };
    reader.readAsDataURL(files[0]);
  }

  const getHeaderButtons = () => (<div className="d-flex gap-2">
    <input data-testid="image-upload-input" type="file" onChange={handleUpload} id="fileUpload" accept="image/*" style={{ display: "none" }} />
    <Button data-testid="stock-photos-button" size="sm" variant="outline-dark" style={{whiteSpace:"nowrap" }} onClick={(e: React.MouseEvent) => { e.preventDefault(); setShowStock(true); }}>Stock</Button>
    <Button data-testid="image-upload-button" size="sm" variant="info" onClick={(e: React.MouseEvent) => { e.preventDefault(); document.getElementById("fileUpload").click(); }}>Upload</Button>
  </div>)

  const cropper = React.useRef(null);

  const onCropperInit = (c: Cropper) => {
    cropper.current = c;

    const el = document.querySelector("#cropperBox .content > div") as HTMLElement;

    const currentWidth = el.clientWidth
    const desiredHeight = props.aspectRatio ? currentWidth / props.aspectRatio : currentWidth / 16 * 9;
    el.style.height = desiredHeight.toString() + "px";
  }

  const onCropperReady = () => {
    // Called when cropper is fully initialized and ready
    // The useEffect will handle calling selectDefaultCropZone when needed
  }

  const selectDefaultCropZone = () => {
    if (cropper.current) {
      const canvasData = cropper.current.getCanvasData();
      const imageData = cropper.current.getImageData();
      const containerData = cropper.current.getContainerData();
      const imgAspect = imageData.aspectRatio;
      const desiredAspect = props.aspectRatio || 16 / 9;

      // Use canvas dimensions which represent the actual drawable image area
      let cropWidth, cropHeight;

      // Compare image aspect ratio to desired aspect ratio
      if (imgAspect > desiredAspect) {
        // Image is wider than desired aspect - constrain by height
        // Use full canvas height, calculate width based on desired aspect
        cropHeight = canvasData.height;
        cropWidth = cropHeight * desiredAspect;
      } else {
        // Image is taller than (or equal to) desired aspect - constrain by width
        // Use full canvas width, calculate height based on desired aspect
        cropWidth = canvasData.width;
        cropHeight = cropWidth / desiredAspect;
      }

      // Center the crop box on the canvas
      const left = canvasData.left + (canvasData.width - cropWidth) / 2;
      const top = canvasData.top + (canvasData.height - cropHeight) / 2;

      cropper.current.setCropBoxData({ width: cropWidth, height: cropHeight, left, top });
    }
  }

  const cropCallback = () => {
    if (cropper.current !== null) {
      //const data = cropper.current.getCropBoxData();
      //const ratio = parseInt((300.0 / data.height).toString());
      //const width = data.width * ratio;
      let url = cropper.current.getCroppedCanvas({ maxWidth: 1920, maxHeight: 1920 }).toDataURL();
      setDataUrl(url);
    }
  }

  const handleCrop = () => {
    if (timeout !== null) {
      window.clearTimeout(timeout);
      timeout = null;
    }
    timeout = window.setTimeout(cropCallback, 200);
  }

  const handleStockSelected = (url: string) => {
    setCurrentUrl(url);
    setDataUrl(url);
    setTimeout(selectDefaultCropZone, 500);
    setShowStock(false);
  }

  const handleSave = () => { props.updatedFunction(dataUrl); }
  const handleCancel = () => { props.updatedFunction(null); }
  const init = useCallback(() => { setCurrentUrl(props.startingUrl) }, []); // eslint-disable-line

  React.useEffect(init, []); // eslint-disable-line

  // When currentUrl changes to a real image, select the default crop zone
  React.useEffect(() => {
    if (currentUrl && currentUrl !== "about:blank" && cropper.current) {
      // Use a longer timeout to ensure cropper is fully ready with data
      setTimeout(() => {
        if (cropper.current) {
          const canvasData = cropper.current.getCanvasData();
          // Only proceed if canvas data is actually available (has width/height)
          if (canvasData && canvasData.width && canvasData.height) {
            selectDefaultCropZone();
          } else {
            // If data not ready yet, try again after another delay
            setTimeout(selectDefaultCropZone, 300);
          }
        }
      }, 500);
    }
  }, [currentUrl]); // eslint-disable-line

  //height: props.height || 400, 
  return (
    <InputBox id="cropperBox" headerIcon="" headerText="Crop" saveFunction={handleSave} saveText={"Update"} cancelFunction={handleCancel} headerActionContent={getHeaderButtons()}>
      <Cropper id="imageCropper"
        onInitialized={onCropperInit}
        ready={onCropperReady}
        src={currentUrl}
        style={{ width: "100%" }}
        aspectRatio={props.aspectRatio}
        guides={false}
        crop={handleCrop}
      />
      {showStock && <StockModal onClose={() => setShowStock(false)} onSelect={handleStockSelected}  />}
    </InputBox>
  );
}
